import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Slider } from '../components/ui/slider';
import { Badge } from '../components/ui/badge';
import { examAPI, testAPI } from '../lib/api';
import api from '../lib/api';
import { 
  ArrowRight, ArrowLeft, Check, Clock, BookOpen, 
  Play, Zap, Timer, Target, ChevronRight, ChevronDown, Info, Lock
} from 'lucide-react';
import { toast } from 'sonner';
import { ButtonSpinner } from '../components/LoadingSpinner';

// Official exam patterns with real configurations
const EXAM_PATTERNS = {
  'JEE Main': {
    totalQuestions: 75,
    totalMarks: 300,
    duration: 180, // 3 hours
    subjects: [
      { 
        name: 'Physics', 
        sections: [
          { name: 'Section A', type: 'MCQ', questions: 20, marks: 4, negative: -1 },
          { name: 'Section B', type: 'Numerical', questions: 5, marks: 4, negative: 0 }
        ],
        totalQuestions: 25, totalMarks: 100
      },
      { 
        name: 'Chemistry', 
        sections: [
          { name: 'Section A', type: 'MCQ', questions: 20, marks: 4, negative: -1 },
          { name: 'Section B', type: 'Numerical', questions: 5, marks: 4, negative: 0 }
        ],
        totalQuestions: 25, totalMarks: 100
      },
      { 
        name: 'Mathematics', 
        sections: [
          { name: 'Section A', type: 'MCQ', questions: 20, marks: 4, negative: -1 },
          { name: 'Section B', type: 'Numerical', questions: 5, marks: 4, negative: 0 }
        ],
        totalQuestions: 25, totalMarks: 100
      }
    ],
    marking: { correct: 4, incorrect: -1, numericalIncorrect: 0 },
    description: 'NTA JEE Main - 75Q (60 MCQ + 15 Numerical), 180 min, +4/-1'
  },
  'JEE Advanced': {
    totalQuestions: 54,
    totalMarks: 183,
    duration: 180,
    subjects: [
      { 
        name: 'Physics', 
        sections: [
          { name: 'Section 1', type: 'MCQ Single', questions: 6, marks: 3, negative: -1 },
          { name: 'Section 2', type: 'MCQ Multiple', questions: 6, marks: 4, negative: -2, partialMarks: true },
          { name: 'Section 3', type: 'Numerical', questions: 6, marks: 3, negative: 0 }
        ],
        totalQuestions: 18, totalMarks: 61
      },
      { 
        name: 'Chemistry', 
        sections: [
          { name: 'Section 1', type: 'MCQ Single', questions: 6, marks: 3, negative: -1 },
          { name: 'Section 2', type: 'MCQ Multiple', questions: 6, marks: 4, negative: -2, partialMarks: true },
          { name: 'Section 3', type: 'Numerical', questions: 6, marks: 3, negative: 0 }
        ],
        totalQuestions: 18, totalMarks: 61
      },
      { 
        name: 'Mathematics', 
        sections: [
          { name: 'Section 1', type: 'MCQ Single', questions: 6, marks: 3, negative: -1 },
          { name: 'Section 2', type: 'MCQ Multiple', questions: 6, marks: 4, negative: -2, partialMarks: true },
          { name: 'Section 3', type: 'Numerical', questions: 6, marks: 3, negative: 0 }
        ],
        totalQuestions: 18, totalMarks: 61
      }
    ],
    marking: { correct: '+3/+4', incorrect: '-1/-2', numerical: 0 },
    description: 'IIT JEE Advanced Paper 1 - 54Q, 183 marks, 180 min'
  },
  'NEET UG': {
    totalQuestions: 180, // 200 provided, 180 to attempt
    totalMarks: 720,
    duration: 200, // 3 hours 20 minutes
    subjects: [
      { 
        name: 'Physics', 
        sections: [
          { name: 'Section A', type: 'MCQ', questions: 35, marks: 4, negative: -1, mandatory: true },
          { name: 'Section B', type: 'MCQ', questions: 10, marks: 4, negative: -1, optional: 15, note: 'Attempt any 10 of 15' }
        ],
        totalQuestions: 45, totalMarks: 180
      },
      { 
        name: 'Chemistry', 
        sections: [
          { name: 'Section A', type: 'MCQ', questions: 35, marks: 4, negative: -1, mandatory: true },
          { name: 'Section B', type: 'MCQ', questions: 10, marks: 4, negative: -1, optional: 15, note: 'Attempt any 10 of 15' }
        ],
        totalQuestions: 45, totalMarks: 180
      },
      { 
        name: 'Botany', 
        sections: [
          { name: 'Section A', type: 'MCQ', questions: 35, marks: 4, negative: -1, mandatory: true },
          { name: 'Section B', type: 'MCQ', questions: 10, marks: 4, negative: -1, optional: 15, note: 'Attempt any 10 of 15' }
        ],
        totalQuestions: 45, totalMarks: 180
      },
      { 
        name: 'Zoology', 
        sections: [
          { name: 'Section A', type: 'MCQ', questions: 35, marks: 4, negative: -1, mandatory: true },
          { name: 'Section B', type: 'MCQ', questions: 10, marks: 4, negative: -1, optional: 15, note: 'Attempt any 10 of 15' }
        ],
        totalQuestions: 45, totalMarks: 180
      }
    ],
    marking: { correct: 4, incorrect: -1 },
    description: 'NTA NEET UG - 180Q (200 provided), 720 marks, 200 min, +4/-1'
  },
  'CAT': {
    totalQuestions: 66,
    totalMarks: 198,
    duration: 120, // 2 hours
    subjects: [
      { 
        name: 'VARC', 
        sections: [
          { name: 'Reading Comprehension', type: 'MCQ', questions: 16, marks: 3, negative: -1 },
          { name: 'Verbal Ability', type: 'TITA', questions: 8, marks: 3, negative: 0 }
        ],
        totalQuestions: 24, totalMarks: 72, timeLimit: 40
      },
      { 
        name: 'DILR', 
        sections: [
          { name: 'Data Interpretation', type: 'MCQ', questions: 10, marks: 3, negative: -1 },
          { name: 'Logical Reasoning', type: 'TITA', questions: 10, marks: 3, negative: 0 }
        ],
        totalQuestions: 20, totalMarks: 60, timeLimit: 40
      },
      { 
        name: 'QA', 
        sections: [
          { name: 'Quantitative Aptitude', type: 'MCQ', questions: 14, marks: 3, negative: -1 },
          { name: 'Quantitative Aptitude', type: 'TITA', questions: 8, marks: 3, negative: 0 }
        ],
        totalQuestions: 22, totalMarks: 66, timeLimit: 40
      }
    ],
    marking: { correct: 3, incorrect: -1, titaIncorrect: 0 },
    description: 'IIM CAT - 66Q, 198 marks, 120 min, +3/-1 (MCQ only)'
  },
  'GATE': {
    totalQuestions: 65,
    totalMarks: 100,
    duration: 180,
    subjects: [
      { 
        name: 'General Aptitude', 
        sections: [
          { name: 'GA', type: 'MCQ+NAT', questions: 10, marks: 15 }
        ],
        totalQuestions: 10, totalMarks: 15
      },
      { 
        name: 'Subject', 
        sections: [
          { name: '1 Mark Questions', type: 'MCQ+NAT', questions: 25, marks: 1 },
          { name: '2 Mark Questions', type: 'MCQ+NAT', questions: 30, marks: 2 }
        ],
        totalQuestions: 55, totalMarks: 85
      }
    ],
    marking: { correct1: 1, correct2: 2, incorrect1: -0.33, incorrect2: -0.66, natIncorrect: 0 },
    description: 'GATE - 65Q, 100 marks, 180 min, -1/3 for 1M, -2/3 for 2M MCQ'
  },
  'SSC CGL': {
    totalQuestions: 100,
    totalMarks: 200,
    duration: 60,
    subjects: [
      { name: 'General Intelligence', totalQuestions: 25, totalMarks: 50 },
      { name: 'General Awareness', totalQuestions: 25, totalMarks: 50 },
      { name: 'Quantitative Aptitude', totalQuestions: 25, totalMarks: 50 },
      { name: 'English Comprehension', totalQuestions: 25, totalMarks: 50 }
    ],
    marking: { correct: 2, incorrect: -0.5 },
    description: 'SSC CGL Tier 1 - 100Q, 200 marks, 60 min, +2/-0.5'
  },
  'UPSC CSE': {
    totalQuestions: 100,
    totalMarks: 200,
    duration: 120,
    subjects: [
      { name: 'Current Affairs', totalQuestions: 25, totalMarks: 50 },
      { name: 'History', totalQuestions: 20, totalMarks: 40 },
      { name: 'Geography', totalQuestions: 20, totalMarks: 40 },
      { name: 'Polity', totalQuestions: 20, totalMarks: 40 },
      { name: 'Economy', totalQuestions: 15, totalMarks: 30 }
    ],
    marking: { correct: 2, incorrect: -0.66 },
    description: 'UPSC Prelims GS-I - 100Q, 200 marks, 120 min, +2/-0.66'
  },
  'BITSAT': {
    totalQuestions: 130,
    totalMarks: 390,
    duration: 180,
    subjects: [
      { name: 'Physics', totalQuestions: 40, totalMarks: 120 },
      { name: 'Chemistry', totalQuestions: 40, totalMarks: 120 },
      { name: 'English Proficiency', totalQuestions: 15, totalMarks: 45 },
      { name: 'Logical Reasoning', totalQuestions: 10, totalMarks: 30 },
      { name: 'Mathematics', totalQuestions: 25, totalMarks: 75 }
    ],
    marking: { correct: 3, incorrect: -1 },
    description: 'BITSAT - 130Q, 390 marks, 180 min, +3/-1'
  },
  'CLAT': {
    totalQuestions: 120,
    totalMarks: 120,
    duration: 120,
    subjects: [
      { name: 'English Language', totalQuestions: 22, totalMarks: 22 },
      { name: 'Current Affairs & GK', totalQuestions: 28, totalMarks: 28 },
      { name: 'Legal Reasoning', totalQuestions: 28, totalMarks: 28 },
      { name: 'Logical Reasoning', totalQuestions: 22, totalMarks: 22 },
      { name: 'Quantitative Techniques', totalQuestions: 13, totalMarks: 13 }
    ],
    marking: { correct: 1, incorrect: -0.25 },
    description: 'CLAT UG - 120Q, 120 marks, 120 min, +1/-0.25'
  },
  'NDA': {
    totalQuestions: 270,
    totalMarks: 900,
    duration: 300, // 2.5 hours each paper
    subjects: [
      { name: 'Mathematics', totalQuestions: 120, totalMarks: 300 },
      { name: 'English', totalQuestions: 50, totalMarks: 200 },
      { name: 'General Knowledge', totalQuestions: 100, totalMarks: 400 }
    ],
    marking: { correct: 2.5, incorrect: -0.83 },
    description: 'NDA - 270Q, 900 marks, 5 hours (2 papers)'
  }
};

// Quick preset configurations
const PRESETS = {
  quick: { questions: 10, duration: 15, label: 'Quick Practice', desc: '10 questions, 15 min' },
  standard: { questions: 30, duration: 45, label: 'Standard Test', desc: '30 questions, 45 min' },
  full: { questions: null, duration: null, label: 'Full Exam', desc: 'Actual exam pattern' }
};

const TestCreate = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [exams, setExams] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [chapters, setChapters] = useState({});
  const [selectedChapterIds, setSelectedChapterIds] = useState([]);
  const [expandedSubjects, setExpandedSubjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [selectedExam, setSelectedExam] = useState(null);
  const [preset, setPreset] = useState('standard');

  const [config, setConfig] = useState({
    exam_id: searchParams?.get('exam') || '',
    subject_ids: [],
    num_questions: 30,
    difficulty: 'mixed',
    custom_duration: 45,
    question_source: 'mixed',
  });

  useEffect(() => {
    loadExams();
  }, []);

  useEffect(() => {
    if (config.exam_id) {
      const exam = exams.find(e => e.id === config.exam_id);
      setSelectedExam(exam);
      loadSubjects(config.exam_id);
    }
  }, [config.exam_id, exams]);

  const loadExams = async () => {
    try {
      const response = await examAPI.getAll();
      setExams(response.data);
    } catch (error) {
      toast.error('Failed to load exams');
    }
  };

  const loadSubjects = async (examId) => {
    try {
      const response = await api.get(`/exams/${examId}/subjects`);
      setSubjects(response.data);
    } catch (error) {
      setSubjects([]);
    }
  };

  const toggleSubject = (subjectId) => {
    setConfig(prev => {
      const wasSelected = prev.subject_ids.includes(subjectId);
      const newSubjectIds = wasSelected
        ? prev.subject_ids.filter(id => id !== subjectId)
        : [...prev.subject_ids, subjectId];
      
      // If deselecting, remove its chapters from selection
      if (wasSelected && chapters[subjectId]) {
        const chapterIdsToRemove = chapters[subjectId].map(c => c.id);
        setSelectedChapterIds(prev => prev.filter(id => !chapterIdsToRemove.includes(id)));
        setExpandedSubjects(prev => prev.filter(id => id !== subjectId));
      }
      
      // If selecting, load chapters if not already loaded
      if (!wasSelected && !chapters[subjectId]) {
        loadChapters(subjectId);
      }
      
      return { ...prev, subject_ids: newSubjectIds };
    });
  };

  const loadChapters = async (subjectId) => {
    try {
      const response = await api.get(`/subjects/${subjectId}/chapters`);
      setChapters(prev => ({ ...prev, [subjectId]: response.data }));
    } catch (error) {
      // Chapters not available for this subject
      setChapters(prev => ({ ...prev, [subjectId]: [] }));
    }
  };

  const toggleChapter = (chapterId) => {
    setSelectedChapterIds(prev =>
      prev.includes(chapterId)
        ? prev.filter(id => id !== chapterId)
        : [...prev, chapterId]
    );
  };

  const toggleExpandSubject = (subjectId) => {
    if (!chapters[subjectId]) loadChapters(subjectId);
    setExpandedSubjects(prev =>
      prev.includes(subjectId)
        ? prev.filter(id => id !== subjectId)
        : [...prev, subjectId]
    );
  };

  const toggleAllChaptersForSubject = (subjectId) => {
    const subjectChapters = chapters[subjectId] || [];
    const allSelected = subjectChapters.every(c => selectedChapterIds.includes(c.id));
    if (allSelected) {
      setSelectedChapterIds(prev => prev.filter(id => !subjectChapters.map(c => c.id).includes(id)));
    } else {
      const newIds = subjectChapters.map(c => c.id).filter(id => !selectedChapterIds.includes(id));
      setSelectedChapterIds(prev => [...prev, ...newIds]);
    }
  };

  // Get official exam pattern if available
  const getExamPattern = () => {
    if (!selectedExam) return null;
    return EXAM_PATTERNS[selectedExam.name] || null;
  };

  const applyPreset = (presetKey) => {
    setPreset(presetKey);
    const p = PRESETS[presetKey];
    const pattern = getExamPattern();
    
    if (presetKey === 'full') {
      // Use official exam pattern if available
      if (pattern) {
        setConfig(prev => ({
          ...prev,
          num_questions: pattern.totalQuestions,
          custom_duration: pattern.duration,
          use_exam_pattern: true
        }));
      } else if (selectedExam?.config) {
        setConfig(prev => ({
          ...prev,
          num_questions: selectedExam.config.total_questions,
          custom_duration: selectedExam.config.duration_minutes,
          use_exam_pattern: true
        }));
      }
    } else {
      setConfig(prev => ({
        ...prev,
        num_questions: p.questions,
        custom_duration: p.duration,
        use_exam_pattern: false
      }));
    }
  };

  const handleStartTest = async () => {
    if (!config.exam_id) {
      toast.error('Please select an exam');
      return;
    }

    setLoading(true);
    try {
      const subjectIds = config.subject_ids.length > 0 
        ? config.subject_ids 
        : subjects.map(s => s.id);
      
      const response = await testAPI.generate({
        ...config,
        subject_ids: subjectIds,
        chapter_ids: selectedChapterIds.length > 0 ? selectedChapterIds : undefined,
      });
      
      toast.success('Test created! Starting now...');
      navigate(`/test/${response.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create test');
    } finally {
      setLoading(false);
    }
  };

  // Step 1: Select Exam
  const renderStep1 = () => {
    const availableExams = exams.filter(exam => exam.is_available);
    const comingSoonExams = exams.filter(exam => !exam.is_available);
    
    return (
    <div className="space-y-8">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold mb-2">Select Your Exam</h2>
        <p className="text-muted-foreground">Choose the exam you want to practice for</p>
      </div>

      {/* Available Exams */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-2xl mx-auto">
        {availableExams.map((exam) => (
          <button
            key={exam.id}
            onClick={() => {
              setConfig({ ...config, exam_id: exam.id });
              setSelectedExam(exam);
              setStep(2);
            }}
            className={`p-6 rounded-2xl border-2 text-left transition-all hover:shadow-lg group ${
              config.exam_id === exam.id
                ? 'border-primary bg-primary/5'
                : 'border-blue-200 dark:border-blue-800 hover:border-blue-400 bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20'
            }`}
          >
            <div className="text-5xl mb-3">{exam.icon || '📚'}</div>
            <h3 className="text-xl font-bold text-foreground group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">{exam.name}</h3>
            {exam.config && (
              <p className="text-sm text-muted-foreground mt-2">
                {exam.config.total_questions} Questions • {exam.config.duration_minutes} min
              </p>
            )}
            <div className="flex items-center justify-between mt-4">
              <Badge className="bg-green-500/20 text-green-600 dark:text-green-400 border-green-500/30">Available</Badge>
              <ArrowRight className="text-blue-600 dark:text-blue-400 group-hover:translate-x-1 transition-transform" size={20} />
            </div>
          </button>
        ))}
      </div>

      {/* Coming Soon Exams */}
      {comingSoonExams.length > 0 && (
        <>
          <div className="text-center pt-6 border-t border-border">
            <h3 className="text-lg font-semibold text-muted-foreground mb-2">Coming Soon</h3>
            <p className="text-sm text-muted-foreground mb-4">More exams will be available soon</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 max-w-3xl mx-auto">
            {comingSoonExams.map((exam) => (
              <div
                key={exam.id}
                className="p-3 rounded-xl border border-border bg-muted/30 opacity-60 cursor-not-allowed relative"
              >
                <div className="absolute top-2 right-2">
                  <Lock size={14} className="text-muted-foreground" />
                </div>
                <div className="text-2xl mb-1 grayscale">{exam.icon || '📚'}</div>
                <h3 className="text-xs font-medium text-muted-foreground">{exam.name}</h3>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  )};

  // Step 2: Configure Test
  const renderStep2 = () => {
    const pattern = getExamPattern();
    
    return (
    <div className="space-y-8">
      {/* Selected Exam Header */}
      <div className="flex items-center justify-between p-4 rounded-xl bg-muted/50 border">
        <div className="flex items-center gap-4">
          <span className="text-4xl">{selectedExam?.icon || '📚'}</span>
          <div>
            <h2 className="text-xl font-bold">{selectedExam?.name}</h2>
            {pattern ? (
              <p className="text-sm text-muted-foreground">
                {pattern.description}
              </p>
            ) : selectedExam?.config && (
              <p className="text-sm text-muted-foreground">
                Full exam: {selectedExam.config.total_questions}Q • {selectedExam.config.duration_minutes}min
              </p>
            )}
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={() => setStep(1)}>
          Change
        </Button>
      </div>

      {/* Official Exam Pattern Info */}
      {pattern && preset === 'full' && (
        <div className="p-4 rounded-xl border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20">
          <h3 className="font-semibold mb-3 flex items-center gap-2 text-blue-700 dark:text-blue-300">
            <Info size={18} />
            Official Exam Pattern
          </h3>
          <div className="grid grid-cols-4 gap-3 mb-4">
            <div className="text-center p-2 bg-white dark:bg-gray-800 rounded-lg">
              <div className="text-lg font-bold text-primary">{pattern.totalQuestions}</div>
              <div className="text-xs text-muted-foreground">Questions</div>
            </div>
            <div className="text-center p-2 bg-white dark:bg-gray-800 rounded-lg">
              <div className="text-lg font-bold text-primary">{pattern.totalMarks}</div>
              <div className="text-xs text-muted-foreground">Total Marks</div>
            </div>
            <div className="text-center p-2 bg-white dark:bg-gray-800 rounded-lg">
              <div className="text-lg font-bold text-primary">{pattern.duration}</div>
              <div className="text-xs text-muted-foreground">Minutes</div>
            </div>
            <div className="text-center p-2 bg-white dark:bg-gray-800 rounded-lg">
              <div className="text-lg font-bold text-primary">
                +{pattern.marking.correct}/{pattern.marking.incorrect}
              </div>
              <div className="text-xs text-muted-foreground">Marking</div>
            </div>
          </div>
          
          {/* Subject-wise breakdown */}
          {pattern.subjects && (
            <div className="space-y-2">
              <h4 className="text-sm font-medium text-blue-700 dark:text-blue-300">Subject-wise Distribution:</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {pattern.subjects.map((subj, idx) => (
                  <div key={idx} className="p-2 bg-white dark:bg-gray-800 rounded-lg text-sm">
                    <div className="font-medium">{subj.name}</div>
                    <div className="text-xs text-muted-foreground">
                      {subj.totalQuestions}Q • {subj.totalMarks} marks
                      {subj.sections && subj.sections.map((sec, i) => (
                        <span key={i} className="ml-1">
                          ({sec.questions} {sec.type})
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Quick Presets */}
      <div>
        <h3 className="font-semibold mb-3 flex items-center gap-2">
          <Zap size={18} />
          Quick Presets
        </h3>
        <div className="grid grid-cols-3 gap-3">
          {Object.entries(PRESETS).map(([key, p]) => {
            const presetPattern = pattern || selectedExam?.config;
            return (
            <button
              key={key}
              onClick={() => applyPreset(key)}
              className={`p-4 rounded-xl border-2 text-center transition-all ${
                preset === key
                  ? 'border-primary bg-primary/5'
                  : 'border-border hover:border-primary/50'
              }`}
            >
              <div className="font-semibold">{p.label}</div>
              <div className="text-xs text-muted-foreground">
                {key === 'full' && pattern 
                  ? `${pattern.totalQuestions}Q, ${pattern.duration}min`
                  : p.desc
                }
              </div>
            </button>
          )})}
        </div>
      </div>

      {/* Subjects Selection */}
      {subjects.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold flex items-center gap-2">
              <BookOpen size={18} />
              Subjects
            </h3>
            <span className="text-sm text-muted-foreground">
              {config.subject_ids.length === 0 ? 'All subjects' : `${config.subject_ids.length} selected`}
            </span>
          </div>
          <div className="flex flex-wrap gap-2">
            {subjects.map((subject) => {
              const isSelected = config.subject_ids.length === 0 || config.subject_ids.includes(subject.id);
              // Get subject info from pattern
              const subjectPattern = pattern?.subjects?.find(s => 
                s.name.toLowerCase() === subject.name.toLowerCase()
              );
              return (
                <button
                  key={subject.id}
                  onClick={() => toggleSubject(subject.id)}
                  className={`px-4 py-2 rounded-lg border-2 transition-all ${
                    isSelected
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-border text-muted-foreground hover:border-primary/50'
                  }`}
                >
                  {subject.name}
                  {subjectPattern && (
                    <span className="text-xs ml-1 opacity-70">
                      ({subjectPattern.totalQuestions}Q)
                    </span>
                  )}
                  {isSelected && <Check size={14} className="inline ml-2" />}
                </button>
              );
            })}
          </div>

          {/* Chapter Selection (expandable per subject) */}
          {config.subject_ids.length > 0 && (
            <div className="mt-4 space-y-2">
              <p className="text-xs text-muted-foreground">
                Click a subject below to pick specific chapters (optional)
              </p>
              {config.subject_ids.map(subjectId => {
                const subject = subjects.find(s => s.id === subjectId);
                if (!subject) return null;
                const isExpanded = expandedSubjects.includes(subjectId);
                const subjectChapters = chapters[subjectId] || [];
                const selectedCount = subjectChapters.filter(c => selectedChapterIds.includes(c.id)).length;
                const allSelected = subjectChapters.length > 0 && subjectChapters.every(c => selectedChapterIds.includes(c.id));
                return (
                  <div key={subjectId} className="border rounded-lg overflow-hidden">
                    <button
                      onClick={() => toggleExpandSubject(subjectId)}
                      className="w-full flex items-center justify-between px-3 py-2 hover:bg-muted/50 transition-colors"
                    >
                      <span className="font-medium text-sm">{subject.name}</span>
                      <div className="flex items-center gap-2">
                        {selectedCount > 0 && (
                          <Badge variant="secondary" className="text-xs">
                            {selectedCount}/{subjectChapters.length} chapters
                          </Badge>
                        )}
                        {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                      </div>
                    </button>
                    {isExpanded && (
                      <div className="px-3 pb-3 border-t">
                        {subjectChapters.length === 0 ? (
                          <p className="text-xs text-muted-foreground py-2">Loading chapters...</p>
                        ) : (
                          <>
                            <button
                              onClick={() => toggleAllChaptersForSubject(subjectId)}
                              className="text-xs text-primary hover:underline mt-2 mb-1"
                            >
                              {allSelected ? 'Deselect all' : 'Select all'}
                            </button>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 mt-1">
                              {subjectChapters.map(chapter => {
                                const isSel = selectedChapterIds.includes(chapter.id);
                                return (
                                  <button
                                    key={chapter.id}
                                    onClick={() => toggleChapter(chapter.id)}
                                    className={`flex items-center gap-2 px-2 py-1.5 rounded text-xs text-left transition-all ${
                                      isSel
                                        ? 'bg-primary/10 text-primary'
                                        : 'hover:bg-muted/50 text-muted-foreground'
                                    }`}
                                  >
                                    <div className={`w-3.5 h-3.5 rounded border flex items-center justify-center flex-shrink-0 ${
                                      isSel ? 'bg-primary border-primary' : 'border-muted-foreground/40'
                                    }`}>
                                      {isSel && <Check size={10} className="text-primary-foreground" />}
                                    </div>
                                    {chapter.name}
                                  </button>
                                );
                              })}
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Custom Settings */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Questions */}
        <div className="p-4 rounded-xl border">
          <div className="flex justify-between mb-3">
            <h3 className="font-semibold">Questions</h3>
            <span className="text-primary font-bold">{config.num_questions}</span>
          </div>
          <Slider
            value={[config.num_questions]}
            onValueChange={([v]) => setConfig({ ...config, num_questions: v })}
            min={5}
            max={selectedExam?.config?.total_questions || 100}
            step={5}
          />
          <div className="flex justify-between mt-2 text-xs text-muted-foreground">
            <span>5</span>
            <span>{selectedExam?.config?.total_questions || 100}</span>
          </div>
        </div>

        {/* Duration */}
        <div className="p-4 rounded-xl border">
          <div className="flex justify-between mb-3">
            <h3 className="font-semibold flex items-center gap-2">
              <Clock size={16} />
              Duration
            </h3>
            <span className="text-primary font-bold">{config.custom_duration} min</span>
          </div>
          <Slider
            value={[config.custom_duration]}
            onValueChange={([v]) => setConfig({ ...config, custom_duration: v })}
            min={10}
            max={selectedExam?.config?.duration_minutes || 180}
            step={5}
          />
          <div className="flex justify-between mt-2 text-xs text-muted-foreground">
            <span>10 min</span>
            <span>{selectedExam?.config?.duration_minutes || 180} min</span>
          </div>
        </div>
      </div>

      {/* Difficulty & Source */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Difficulty */}
        <div className="p-4 rounded-xl border">
          <h3 className="font-semibold mb-3">Difficulty</h3>
          <div className="grid grid-cols-2 gap-2">
            {['easy', 'medium', 'hard', 'mixed'].map((d) => (
              <button
                key={d}
                onClick={() => setConfig({ ...config, difficulty: d })}
                className={`px-3 py-2 rounded-lg border text-sm capitalize transition-all ${
                  config.difficulty === d
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-border hover:border-primary/50'
                }`}
              >
                {d === 'easy' && '🌱 '}{d === 'medium' && '📊 '}{d === 'hard' && '🔥 '}{d === 'mixed' && '🎯 '}
                {d}
              </button>
            ))}
          </div>
        </div>

        {/* Question Source */}
        <div className="p-4 rounded-xl border">
          <h3 className="font-semibold mb-3">Question Source</h3>
          <div className="grid grid-cols-3 gap-2">
            {[
              { key: 'pyq', label: '📖 PYQ' },
              { key: 'important', label: '⭐ Important' },
              { key: 'mixed', label: '🎯 Mixed' }
            ].map((s) => (
              <button
                key={s.key}
                onClick={() => setConfig({ ...config, question_source: s.key })}
                className={`px-3 py-2 rounded-lg border text-sm transition-all ${
                  config.question_source === s.key
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-border hover:border-primary/50'
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex gap-4 pt-4">
        <Button variant="outline" onClick={() => setStep(1)} className="flex-1">
          <ArrowLeft size={18} className="mr-2" />
          Back
        </Button>
        <Button onClick={() => setStep(3)} className="flex-1">
          Review
          <ArrowRight size={18} className="ml-2" />
        </Button>
      </div>
    </div>
  );
  };

  // Step 3: Review & Start
  const renderStep3 = () => {
    const pattern = getExamPattern(selectedExam?.name);
    const isFullTest = config.preset === 'full' && pattern;
    
    return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold mb-2">Ready to Start?</h2>
        <p className="text-muted-foreground">
          {isFullTest ? 'Official Exam Simulation' : 'Review your test configuration'}
        </p>
      </div>

      {/* Official Exam Badge for Full Test */}
      {isFullTest && (
        <div className="p-4 rounded-xl bg-gradient-to-r from-emerald-500/10 via-blue-500/10 to-purple-500/10 border border-emerald-500/30">
          <div className="flex items-center justify-center gap-2">
            <span className="text-2xl">🎯</span>
            <span className="font-bold text-lg">Official {selectedExam?.name} Pattern</span>
            <span className="px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 text-xs font-semibold">
              REAL EXAM
            </span>
          </div>
        </div>
      )}

      {/* Summary Card */}
      <div className="p-6 rounded-xl border bg-muted/30">
        <div className="flex items-center gap-4 mb-6 pb-6 border-b">
          <span className="text-5xl">{selectedExam?.icon || '📚'}</span>
          <div>
            <h3 className="text-2xl font-bold">{selectedExam?.name}</h3>
            <p className="text-muted-foreground">
              {isFullTest ? 'Full Test - Official Pattern' : 'Practice Test'}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center p-3 rounded-lg bg-background">
            <div className="text-2xl font-bold text-primary">{config.num_questions}</div>
            <div className="text-sm text-muted-foreground">Questions</div>
          </div>
          <div className="text-center p-3 rounded-lg bg-background">
            <div className="text-2xl font-bold text-primary">{config.custom_duration}m</div>
            <div className="text-sm text-muted-foreground">Duration</div>
          </div>
          <div className="text-center p-3 rounded-lg bg-background">
            <div className="text-2xl font-bold text-primary">
              {isFullTest ? pattern.totalMarks : config.difficulty}
            </div>
            <div className="text-sm text-muted-foreground">
              {isFullTest ? 'Total Marks' : 'Difficulty'}
            </div>
          </div>
          <div className="text-center p-3 rounded-lg bg-background">
            <div className="text-2xl font-bold text-primary">
              {isFullTest ? `+${pattern.subjects[0]?.sections?.[0]?.correct || pattern.subjects[0]?.correct || 4}/-${Math.abs(pattern.subjects[0]?.sections?.[0]?.incorrect || pattern.subjects[0]?.incorrect || 1)}` : (config.subject_ids.length || subjects.length)}
            </div>
            <div className="text-sm text-muted-foreground">
              {isFullTest ? 'Marking' : 'Subjects'}
            </div>
          </div>
        </div>

        {/* Official Pattern Breakdown */}
        {isFullTest && pattern.subjects && (
          <div className="mt-6 p-4 rounded-lg bg-background border">
            <h4 className="font-semibold mb-3 text-sm">📋 Section-wise Breakdown:</h4>
            <div className="space-y-2">
              {pattern.subjects.map((subj, idx) => (
                <div key={idx} className="flex justify-between items-center text-sm">
                  <span className="font-medium">{subj.name}</span>
                  <span className="text-muted-foreground">
                    {subj.sections ? (
                      subj.sections.map((s, i) => (
                        <span key={i}>
                          {s.questions} {s.type}
                          {i < subj.sections.length - 1 && ' + '}
                        </span>
                      ))
                    ) : (
                      `${subj.questions} questions`
                    )}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="mt-6 p-4 rounded-lg bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800">
          <p className="text-sm text-amber-700 dark:text-amber-300">
            ⏱️ You'll have approximately <strong>{Math.round(config.custom_duration / config.num_questions * 60)}s</strong> per question. Good luck!
          </p>
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-4">
        <Button variant="outline" onClick={() => setStep(2)} className="flex-1">
          <ArrowLeft size={18} className="mr-2" />
          Edit
        </Button>
        <Button 
          onClick={handleStartTest} 
          disabled={loading}
          className="flex-1 h-12 bg-emerald-600 hover:bg-emerald-700"
        >
          {loading ? (
            <>
              <ButtonSpinner size={16} className="mr-2" />
              Creating...
            </>
          ) : (
            <>
              <Play size={18} className="mr-2" />
              Start Test
            </>
          )}
        </Button>
      </div>
    </div>
  );
  };

  return (
    <Layout>
      <div className="min-h-screen bg-background px-4 py-8">
        <div className="max-w-4xl mx-auto">
          {/* Progress Steps */}
          <div className="flex items-center justify-center gap-2 mb-12">
            {[
              { num: 1, label: 'Select Exam' },
              { num: 2, label: 'Configure' },
              { num: 3, label: 'Start' }
            ].map((s, i) => (
              <React.Fragment key={s.num}>
                <button
                  onClick={() => s.num <= step && setStep(s.num)}
                  disabled={s.num > step}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full transition-all ${
                    s.num === step
                      ? 'bg-primary text-primary-foreground'
                      : s.num < step
                      ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400'
                      : 'bg-muted text-muted-foreground'
                  }`}
                >
                  <span className="w-6 h-6 rounded-full bg-current/20 flex items-center justify-center text-sm font-medium">
                    {s.num < step ? <Check size={14} /> : s.num}
                  </span>
                  <span className="hidden sm:inline text-sm font-medium">{s.label}</span>
                </button>
                {i < 2 && (
                  <ChevronRight size={20} className={s.num < step ? 'text-emerald-500' : 'text-muted-foreground'} />
                )}
              </React.Fragment>
            ))}
          </div>

          {/* Step Content */}
          {step === 1 && renderStep1()}
          {step === 2 && renderStep2()}
          {step === 3 && renderStep3()}
        </div>
      </div>
    </Layout>
  );
};

export default TestCreate;
